/**
 * return-to.ts — carry the page a staff member ASKED FOR across the sign-in they
 * were bounced through (UAT fail 1b, 31 July 2026).
 *
 * ⛔⛔ THE DEFECT THIS CLOSES IS NOT A ROUTING BUG. `/admin/users/{sub}/access`
 * resolved perfectly: the route existed, `PersonAccessRedirect` rewrote it to
 * `/admin/users?tab=subscribers&open={sub}`, and the drilldown opened. Rendering
 * the real `App` at that URL proves it (`person-access-deep-link.test.tsx`). What
 * failed was everything BEFORE the router got a say:
 *
 *   1. Amplify holds staff tokens in **sessionStorage**, which is per-TAB.
 *      Opening a shared or bookmarked link in a NEW TAB has no session.
 *   2. `AuthenticatedRoute` then rendered `<Navigate to="/signin" replace />`,
 *      **discarding the location entirely**.
 *   3. `SignIn` finished and hard-navigated to `/`.
 *
 * So the staff member landed on the dashboard — which is exactly what UAT
 * reported, and which happens to EVERY deep link in this portal, not just this
 * one. The URL was never wrong; nothing was carrying it.
 *
 * ⭐ WHY LOCATION STATE RATHER THAN `?next=`. A query parameter would survive a
 * reload of `/signin`, which state does not — but it is also attacker-supplied
 * text on the sign-in page, i.e. an open-redirect surface that has to be
 * validated forever after. The bounce here is a single client-side transition
 * inside one SPA session (`<Navigate>` → `SignIn` → `navigate`), with no full
 * page load in between, so state survives the whole flow. The value never leaves
 * the router.
 *
 * ⚠ IT IS VALIDATED ANYWAY. `safeReturnTo` is the only reader, and it accepts a
 * same-origin ABSOLUTE PATH and nothing else. Defence in depth costs one regex,
 * and the alternative is trusting that no future edit ever puts a URL into this
 * state from somewhere less controlled.
 */
import type { Location } from 'react-router-dom';

/** The router-state shape `AuthenticatedRoute` writes and `SignIn` reads. */
export interface ReturnToState {
  /** Absolute path (+ search + hash) the user originally asked for. */
  returnTo?: string;
}

/** Where a staff member goes when there is nothing to return to. */
export const DEFAULT_LANDING = '/';

/** Flatten a router location into the path this module carries. */
export function locationToReturnTo(location: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * The requested path, if it is one we are willing to send a browser to.
 *
 * ⛔ REJECTS ANYTHING THAT IS NOT A SAME-ORIGIN ABSOLUTE PATH. In particular
 * `//evil.example` — which is a PROTOCOL-RELATIVE URL, not a path, and is the
 * classic way a naive `startsWith('/')` check becomes an open redirect. Also
 * rejects `/signin` itself, which would loop.
 */
export function safeReturnTo(state: unknown): string {
  const raw = (state as ReturnToState | null)?.returnTo;
  if (typeof raw !== 'string' || raw.length === 0) return DEFAULT_LANDING;
  if (!raw.startsWith('/') || raw.startsWith('//')) return DEFAULT_LANDING;
  // A backslash is treated as a slash by some browsers when resolving URLs, so
  // `/\evil.example` can escape the origin the same way `//` does.
  if (raw.startsWith('/\\')) return DEFAULT_LANDING;
  if (raw === '/signin' || raw.startsWith('/signin?')) return DEFAULT_LANDING;
  return raw;
}
